// Terms.jsx
import React from 'react';
import styles from './terms.module.css';

// Material UI Icons
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import HouseIcon from '@mui/icons-material/House';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
// import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import UpdateIcon from '@mui/icons-material/Update';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
// import AssignmentIcon from '@mui/icons-material/Assignment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

const Terms = () => {
  return (
    <div className={styles.termsPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <GavelIcon className={styles.heroIcon} />
          <h1>Terms & Conditions</h1>
          <div className={styles.heroLine}></div>
          <p>Please read these terms carefully before using our services</p>
          <div className={styles.lastUpdated}>
            <UpdateIcon />
            <span>Last Updated: January 15, 2024</span>
          </div>
        </div>
      </section>

      {/* Content Container */}
      <div className={styles.container}>
        {/* Quick Navigation */}
        <div className={styles.quickNav}>
          <h3>Jump to Section</h3>
          <div className={styles.navLinks}>
            <a href="#agreement">Agreement</a>
            <a href="#booking">Booking & Reservations</a>
            <a href="#payment">Payments</a>
            <a href="#cancellation">Cancellation Policy</a>
            <a href="#rules">House Rules</a>
            <a href="#liability">Liability</a>
            <a href="#termination">Termination</a>
            <a href="#contact">Contact Us</a>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.content}>
          {/* Introduction */}
          <section id="agreement" className={styles.section}>
            <div className={styles.sectionHeader}>
              <DescriptionIcon />
              <h2>Agreement to Terms</h2>
            </div>
            <p>
              By accessing or using the Equity Residences website, applying for accommodation, 
              or signing a lease agreement, you agree to be bound by these Terms & Conditions 
              and our Privacy Policy. If you disagree with any part of these terms, you may 
              not access our services.
            </p>
            <p>
              These terms apply to all visitors, users, residents, and others who access or 
              use our services. Equity Residences reserves the right to modify these terms 
              at any time without prior notice.
            </p>
          </section>

          {/* Booking & Reservations */}
          <section id="booking" className={styles.section}>
            <div className={styles.sectionHeader}>
              <HouseIcon />
              <h2>Booking & Reservations</h2>
            </div>
            <div className={styles.bookingInfo}>
              <div className={styles.bookingItem}>
                <CheckCircleIcon />
                <div>
                  <strong>Application Process</strong>
                  <p>All applicants must complete our online application form and provide valid student identification. Applications are subject to approval based on availability and eligibility criteria.</p>
                </div>
              </div>
              <div className={styles.bookingItem}>
                <CheckCircleIcon />
                <div>
                  <strong>Lease Agreement</strong>
                  <p>Upon approval, you will be required to sign a formal lease agreement. The lease outlines the terms of your stay, including duration, payment schedule, and house rules.</p>
                </div>
              </div>
              <div className={styles.bookingItem}>
                <CheckCircleIcon />
                <div>
                  <strong>Security Deposit</strong>
                  <p>A security deposit is required to secure your reservation. The deposit will be refunded within 30 days of lease end, subject to inspection and any deductions for damages.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Payment Terms */}
          <section id="payment" className={styles.section}>
            <div className={styles.sectionHeader}>
              <PaymentIcon />
              <h2>Payment Terms</h2>
            </div>
            <div className={styles.paymentInfo}>
              <p>All payments must be made in South African Rand (ZAR) via the following methods:</p>
              <ul>
                <li>Bank Transfer (EFT)</li>
                <li>Credit/Debit Card</li>
                <li>Cash Deposit (at designated locations)</li>
              </ul>
              <div className={styles.note}>
                <strong>Late Payments:</strong> A late fee of 10% will be applied to payments not received by the due date. Repeated late payments may result in lease termination.
              </div>
            </div>
          </section>

          {/* Cancellation Policy */}
          <section id="cancellation" className={styles.section}>
            <div className={styles.sectionHeader}>
              <CancelIcon />
              <h2>Cancellation Policy</h2>
            </div>
            <div className={styles.cancellationGrid}>
              <div className={styles.cancellationCard}>
                <h4>Within 14 Days of Booking</h4>
                <p>Full refund of deposit and any payments made.</p>
              </div>
              <div className={styles.cancellationCard}>
                <h4>15-30 Days Before Move-in</h4>
                <p>50% refund of deposit. Other payments fully refunded.</p>
              </div>
              <div className={styles.cancellationCard}>
                <h4>Less than 14 Days Before Move-in</h4>
                <p>No refund of deposit. Other payments partially refunded.</p>
              </div>
              <div className={styles.cancellationCard}>
                <h4>After Move-in Date</h4>
                <p>Cancellation subject to 30-day notice. Deposit forfeited.</p>
              </div>
            </div>
            <div className={styles.note}>
              <strong>Note:</strong> Special circumstances (medical emergencies, visa denials) may qualify for exceptions. Please contact management for review.
            </div>
          </section>

          {/* House Rules */}
          <section id="rules" className={styles.section}>
            <div className={styles.sectionHeader}>
              <SecurityIcon />
              <h2>House Rules</h2>
            </div>
            <p>All residents and guests must adhere to the following house rules:</p>
            <div className={styles.rulesGrid}>
              <div className={styles.ruleItem}>
                <span className={styles.ruleNumber}>01</span>
                <div>
                  <strong>Quiet Hours</strong>
                  <p>10:00 PM - 7:00 AM daily. No excessive noise during these hours.</p>
                </div>
              </div>
              <div className={styles.ruleItem}>
                <span className={styles.ruleNumber}>02</span>
                <div>
                  <strong>No Smoking</strong>
                  <p>Smoking is prohibited inside all buildings. Designated outdoor areas available.</p>
                </div>
              </div>
              <div className={styles.ruleItem}>
                <span className={styles.ruleNumber}>03</span>
                <div>
                  <strong>Guests Policy</strong>
                  <p>Guests must be accompanied by residents. Overnight guests is not permitted.</p>
                </div>
              </div>
              <div className={styles.ruleItem}>
                <span className={styles.ruleNumber}>04</span>
                <div>
                  <strong>Common Areas</strong>
                  <p>Keep shared spaces clean. No personal items left unattended.</p>
                </div>
              </div>
              <div className={styles.ruleItem}>
                <span className={styles.ruleNumber}>05</span>
                <div>
                  <strong>Pets</strong>
                  <p>No pets allowed unless approved for medical/emotional support with documentation.</p>
                </div>
              </div>
              <div className={styles.ruleItem}>
                <span className={styles.ruleNumber}>06</span>
                <div>
                  <strong>Maintenance</strong>
                  <p>Report maintenance issues promptly. Do not attempt DIY repairs.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Liability */}
          <section id="liability" className={styles.section}>
            <div className={styles.sectionHeader}>
              <WarningIcon />
              <h2>Limitation of Liability</h2>
            </div>
            <p>
              Equity Residences shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages resulting from:
            </p>
            <ul className={styles.liabilityList}>
              <li>Loss or theft of personal property</li>
              <li>Injuries occurring on premises (unless due to negligence)</li>
              <li>Interruptions in utility services (water, electricity, internet)</li>
              <li>Actions of other residents or third parties</li>
              <li>Events beyond our reasonable control (force majeure)</li>
            </ul>
            <div className={styles.note}>
              <strong>Important:</strong> Residents are strongly advised to obtain renters insurance to protect personal belongings.
            </div>
          </section>

          {/* Termination */}
          <section id="termination" className={styles.section}>
            <div className={styles.sectionHeader}>
              <BlockIcon />
              <h2>Termination of Agreement</h2>
            </div>
            <p>
              Equity Reserves the right to terminate this agreement immediately for violations including:
            </p>
            <div className={styles.terminationGrid}>
              <div className={styles.terminationCard}>
                <ReportProblemIcon />
                <span>Non-payment of rent or fees</span>
              </div>
              <div className={styles.terminationCard}>
                <ReportProblemIcon />
                <span>Illegal activities on premises</span>
              </div>
              <div className={styles.terminationCard}>
                <ReportProblemIcon />
                <span>Repeated noise complaints</span>
              </div>
              <div className={styles.terminationCard}>
                <ReportProblemIcon />
                <span>Property damage or vandalism</span>
              </div>
              <div className={styles.terminationCard}>
                <ReportProblemIcon />
                <span>Subletting without permission</span>
              </div>
              <div className={styles.terminationCard}>
                <ReportProblemIcon />
                <span>Violation of house rules</span>
              </div>
            </div>
          </section>

          {/* Governing Law */}
          <section id="governing" className={styles.section}>
            <div className={styles.sectionHeader}>
              <GavelIcon />
              <h2>Governing Law</h2>
            </div>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of 
              the Republic of South Africa. Any disputes arising from these terms shall be 
              subject to the exclusive jurisdiction of the courts of Cape Town, South Africa.
            </p>
          </section>

          {/* Updates */}
          <section id="updates" className={styles.section}>
            <div className={styles.sectionHeader}>
              <UpdateIcon />
              <h2>Updates to Terms</h2>
            </div>
            <p>
              We reserve the right to modify these Terms & Conditions at any time. Changes become 
              effective immediately upon posting. Your continued use of our services after changes 
              constitutes acceptance of the modified terms. We encourage you to review this page 
              periodically for updates.
            </p>
          </section>

          {/* Contact Us */}
          <section id="contact" className={styles.section}>
            <div className={styles.sectionHeader}>
              <ContactSupportIcon />
              <h2>Contact Us</h2>
            </div>
            <p>If you have any questions about these Terms & Conditions, please contact us:</p>
            <div className={styles.contactInfo}>
              <div className={styles.contactMethod}>
                <EmailIcon />
                <div>
                  <strong>Email:</strong>
                  <a href="mailto:info@equityhousing.co.za">info@equityhousing.co.za</a>
                </div>
              </div>
              <div className={styles.contactMethod}>
                <PhoneIcon />
                <div>
                  <strong>Phone:</strong>
                  <a href="tel:+27825808046">+27 82 580 8046</a>
                </div>
              </div>
              <div className={styles.contactMethod}>
                <LocationOnIcon />
                <div>
                  <strong>Address:</strong>
                  <span>94 Zastron Street, Bloemfontein , 9300</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;